/* RESPONSIBLE TEAM: team-knowledge-foundations */
import { type NodeSpec } from 'prosemirror-model';
import type ComposerConfig from '@intercom/composer-ember/lib/config/composer-config';
import type { DynamicContentItem } from 'embercom/lib/articles/composer-config';
import { renderBlocksV1 } from 'embercom/helpers/render-blocks';

type CustomBlockHandler = NonNullable<ComposerConfig['customBlockHandlers']>[number];
type CustomInlineHandler = NonNullable<ComposerConfig['customInlineHandlers']>[number];

interface DynamicContentReferenceBlock {
  type: string;
  dynamicContentKey?: string;
  block_id?: string;
}

const TRIGGER_ICON_PATH =
  'M14.4301 7H10.0001V0.779998C10.0001 0.509998 9.66011 0.370001 9.48011 0.580001L2.35011 8.5C2.18011 8.69 2.31011 9 2.57011 9H7.00011V15.22C7.00011 15.49 7.34011 15.63 7.52011 15.42L14.6501 7.5C14.8201 7.31 14.6901 7 14.4301 7Z';

function createTriggerIcon(size: number, ...classNames: string[]): SVGSVGElement {
  let icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('width', String(size));
  icon.setAttribute('height', String(size));
  icon.setAttribute('viewBox', '0 0 16 16');
  icon.setAttribute('fill', 'currentColor');
  icon.classList.add('flex-shrink-0', ...classNames);
  let path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', TRIGGER_ICON_PATH);
  icon.appendChild(path);
  return icon;
}

function findVariation(dc: DynamicContentItem, articleLocale: string) {
  let variation = dc.variations.find((v) => v.locale === articleLocale);
  if (!variation) {
    variation = dc.variations.find((v) => v.locale === dc.defaultLocale);
  }
  return variation;
}

function createDynamicContentReferenceSpec(
  dcLookup: Map<string, DynamicContentItem>,
  articleLocale: string,
): NodeSpec {
  return {
    inline: false,
    attrs: {
      type: { default: 'dynamicContentReference' },
      dynamicContentKey: { default: '' },
      block_id: { default: '' },
    },
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,
    parseDOM: [
      {
        tag: 'div[data-dynamic-content-reference]',
        getAttrs(dom: HTMLElement) {
          return {
            dynamicContentKey: dom.getAttribute('data-dynamic-content-key') ?? '',
            block_id: dom.getAttribute('data-block-id') ?? '',
          };
        },
      },
    ],
    toDOM(node) {
      let key = String(node.attrs.dynamicContentKey);
      let dc = dcLookup.get(key);

      let wrapper = document.createElement('div');
      wrapper.className =
        'dynamic-content-reference my-2 rounded-lg border border-neutral-border overflow-hidden select-none';
      wrapper.setAttribute('data-dynamic-content-reference', '');
      wrapper.setAttribute('data-dynamic-content-key', key);
      wrapper.setAttribute('data-block-id', String(node.attrs.block_id));
      wrapper.setAttribute('contenteditable', 'false');

      let header = document.createElement('div');
      header.className =
        'flex items-center gap-2 py-1.5 px-3 bg-neutral-container border-b border-neutral-border';

      let icon = createTriggerIcon(16, 'text-accent');

      let nameLabel = document.createElement('span');
      nameLabel.className = 'text-sm font-medium text-default';
      nameLabel.textContent = dc?.name ?? key;

      header.appendChild(icon);
      header.appendChild(nameLabel);
      wrapper.appendChild(header);

      let contentArea = document.createElement('div');
      contentArea.className = 'px-3 py-2';

      if (!dc) {
        let unknown = document.createElement('div');
        unknown.className = 'text-sm text-muted italic';
        unknown.textContent = key;
        contentArea.appendChild(unknown);
      } else {
        let variation = findVariation(dc, articleLocale);
        if (!variation) {
          let noContent = document.createElement('div');
          noContent.className = 'text-sm text-muted italic';
          noContent.textContent = '';
          contentArea.appendChild(noContent);
        } else if (dc.contentType === 'block' && variation.jsonBlocks?.length) {
          let contentWrapper = document.createElement('div');
          contentWrapper.className = 'educate__article-composer mb-0';
          let rendered = renderBlocksV1(variation.jsonBlocks, false, undefined);
          contentWrapper.appendChild(rendered);
          contentArea.appendChild(contentWrapper);
        } else {
          let textEl = document.createElement('p');
          textEl.className = 'text-sm text-default';
          textEl.textContent = variation.textContent;
          contentArea.appendChild(textEl);
        }
      }

      wrapper.appendChild(contentArea);

      return wrapper;
    },
  };
}

export function createDynamicContentReferenceHandler(
  dcItems: DynamicContentItem[] = [],
  articleLocale: string,
): CustomBlockHandler {
  let dcLookup = new Map(dcItems.map((item) => [item.key, item]));

  return {
    type: 'dynamicContentReference',
    spec: createDynamicContentReferenceSpec(dcLookup, articleLocale),
    parse(block, parser) {
      let dcBlock = block as DynamicContentReferenceBlock;
      let spec = parser.schema.nodes.dynamicContentReference;
      if (spec) {
        parser.documentBuilder.openNode(spec, {
          type: dcBlock.type,
          dynamicContentKey: dcBlock.dynamicContentKey ?? '',
          block_id: dcBlock.block_id ?? '',
        });
        parser.documentBuilder.closeNode();
      }
    },
    serialize(node) {
      return {
        type: node.attrs.type as string,
        dynamicContentKey: node.attrs.dynamicContentKey as string,
        block_id: node.attrs.block_id as string,
      };
    },
  };
}

function createDynamicContentTemplateSpec(
  dcLookup: Map<string, DynamicContentItem>,
  articleLocale: string,
): NodeSpec {
  return {
    inline: true,
    attrs: {
      dynamicContentKey: { default: '' },
    },
    group: 'inline',
    atom: true,
    selectable: true,
    parseDOM: [
      {
        tag: 'span.dynamic-content-template',
        getAttrs(dom: HTMLSpanElement) {
          return {
            dynamicContentKey: dom.getAttribute('data-dynamic-content-key') ?? '',
          };
        },
      },
      {
        tag: 'span',
        getAttrs(dom: HTMLSpanElement) {
          let match = (dom.textContent ?? '').match(/^\{\{dynamic_content:([^}]+)\}\}$/);
          if (!match) {
            return false;
          }
          return { dynamicContentKey: match[1] };
        },
      },
    ],
    toDOM(node) {
      let key = String(node.attrs.dynamicContentKey);
      let dc = dcLookup.get(key);
      let displayText = `{{dynamic_content:${key}}}`;

      if (dc) {
        let variation = findVariation(dc, articleLocale);
        if (variation && variation.textContent) {
          displayText = variation.textContent;
        }
      }

      let span = document.createElement('span');
      span.className =
        'dynamic-content-template inline-flex items-center gap-0.5 rounded bg-accent-container text-accent px-1';
      span.setAttribute('data-dynamic-content-key', key);
      span.setAttribute('contenteditable', 'false');

      span.appendChild(createTriggerIcon(12));
      span.appendChild(document.createTextNode(displayText));

      return span;
    },
  };
}

export function createDynamicContentTemplateHandler(
  dcItems: DynamicContentItem[] = [],
  articleLocale: string,
): CustomInlineHandler {
  let dcLookup = new Map(dcItems.map((item) => [item.key, item]));

  return {
    name: 'dynamicContentTemplate',
    spec: createDynamicContentTemplateSpec(dcLookup, articleLocale),
    detect(astNode) {
      if (astNode.value !== 'span') {
        return false;
      }
      if (
        astNode.attributes?.some(
          (attr) =>
            attr.name === 'class' && (attr.value ?? '').includes('dynamic-content-template'),
        )
      ) {
        return true;
      }
      if (astNode.children?.length === 1) {
        let child = astNode.children[0];
        if (!child.isElement && /^\{\{dynamic_content:[^}]+\}\}$/.test(child.value ?? '')) {
          return true;
        }
      }
      return false;
    },
    parse(astNode, parser) {
      let nodeType = parser.schema.nodes.dynamicContentTemplate;
      if (!nodeType) {
        return;
      }
      let attrs = parser.collectAttrs(astNode);
      let key = attrs['data-dynamic-content-key'] ?? '';
      if (!key && astNode.children?.length === 1) {
        let text = astNode.children[0].value ?? '';
        let match = text.match(/^\{\{dynamic_content:([^}]+)\}\}$/);
        if (match) {
          key = match[1];
        }
      }
      parser.documentBuilder.openNode(nodeType, { dynamicContentKey: key });
      parser.documentBuilder.closeNode();
    },
    serialize(node, serializer) {
      let key = serializer.escape(node.attrs.dynamicContentKey as string);
      return `<span>{{dynamic_content:${key}}}</span>`;
    },
  };
}
